/**
 * Radar filter UI constants & metadata (filter-fix-v2).
 *
 * Types are canonical in @/services/api/teacher; this module adds the
 * hardcoded DEFAULT, status-bucket metadata (labels/colors) and small helpers.
 */
import type {
  RadarFilterState,
  RadarStatusBucket,
  RadarZone,
  RadarBubbleType,
  RadarType,
} from '@/services/api/teacher'

export type {
  RadarFilterState,
  RadarStatusBucket,
  RadarZone,
  RadarBubbleType,
  RadarType,
  RadarFilterSpecials,
} from '@/services/api/teacher'

export const ALL_STATUS_BUCKETS: RadarStatusBucket[] = [
  'pending',
  'interested',
  'filled',
  'declined',
  'cancelled',
  'expired',
  'assigned_elsewhere',
]

export const ALL_ZONES: RadarZone[] = ['panic', 'middle', 'outer']
export const ALL_TYPES: RadarBubbleType[] = ['outgoing', 'incoming']

export const DEFAULT_RADAR_FILTER: RadarFilterState = {
  status: [...ALL_STATUS_BUCKETS],
  zones: [...ALL_ZONES],
  types: [...ALL_TYPES],
  subjects: [],
  faculties: [],
  classes: [],
  requesters: [],
  specials: {
    prep_open: false,
    prep_requested: false,
    storno_pending: false,
    director_action_pending: false,
    has_unread: false,
    is_split: false,
    partial: false,
    needs_admin_assignment: false,
    // frost-v2: Filter-Special «Beim Direktor»
    director_gate_pending: false,
  },
  activeOnly: false,
}

/** Teacher radar: Active only, all 3 zones, Anfragen an mich + von mir. */
export const DEFAULT_TEACHER_RADAR_FILTER: RadarFilterState = {
  ...DEFAULT_RADAR_FILTER,
  specials: { ...DEFAULT_RADAR_FILTER.specials },
  status: [...ALL_STATUS_BUCKETS],
  zones: [...ALL_ZONES],
  types: [...ALL_TYPES],
  subjects: [],
  faculties: [],
  classes: [],
  requesters: [],
  activeOnly: true,
}

export function defaultFilterForRadarType(radarType: RadarType): RadarFilterState {
  return radarType === 'teacher' ? DEFAULT_TEACHER_RADAR_FILTER : DEFAULT_RADAR_FILTER
}

export interface StatusBucketMeta {
  bucket: RadarStatusBucket
  labelDe: string
  color: string
  /** Only meaningful in the teacher radar (incoming). */
  teacherOnly?: boolean
}

export const STATUS_BUCKET_META: StatusBucketMeta[] = [
  { bucket: 'pending', labelDe: 'Offen', color: '#f97316' },
  { bucket: 'interested', labelDe: 'Interessiert / Wartepool', color: '#3b82f6' },
  { bucket: 'filled', labelDe: 'Übernommen', color: '#22c55e' },
  { bucket: 'declined', labelDe: 'Abgelehnt', color: '#ef4444' },
  { bucket: 'cancelled', labelDe: 'Storniert', color: '#6b7280' },
  { bucket: 'expired', labelDe: 'Abgelaufen', color: '#6b7280' },
  { bucket: 'assigned_elsewhere', labelDe: 'Von anderer übernommen', color: '#9ca3af', teacherOnly: true },
]

export const ZONE_META: { zone: RadarZone; labelDe: string; color: string }[] = [
  { zone: 'panic', labelDe: 'Panikzone', color: '#ef4444' },
  { zone: 'middle', labelDe: 'Mittelzone', color: '#f97316' },
  { zone: 'outer', labelDe: 'Außenzone', color: '#94a3b8' },
]

export const TYPE_META: { type: RadarBubbleType; labelDe: string }[] = [
  { type: 'outgoing', labelDe: 'Anfragen von mir' },
  { type: 'incoming', labelDe: 'Anfragen an mich' },
]

/** Deep clone a filter state (so callers can't mutate the DEFAULT). */
export function cloneFilterState(state: RadarFilterState): RadarFilterState {
  return {
    status: [...state.status],
    zones: [...state.zones],
    types: [...state.types],
    subjects: [...state.subjects],
    faculties: [...state.faculties],
    classes: [...state.classes],
    requesters: [...state.requesters],
    specials: { ...state.specials },
    activeOnly: state.activeOnly,
  }
}
